import { createDateFormatter, TimeFormatPreference } from "@caredna-marlin/utils/dates";
import { createContext, PropsWithChildren, useContext, useMemo, useState, useEffect, useCallback } from "react";

// localStorage key for time format preference
const TIME_FORMAT_PREFERENCE_KEY = 'medarrive-time-format-preference';

export const loadTimeFormatPreference = (): TimeFormatPreference => {
  try {
    const saved = localStorage.getItem(TIME_FORMAT_PREFERENCE_KEY);
    return saved as TimeFormatPreference ?? TimeFormatPreference.h12;
  } catch (error) {
    console.warn('Failed to load time format preference from localStorage:', error);
  }
  return TimeFormatPreference.h12; // default to standard time
};

const saveTimeFormatToLocalStorage = (preference: TimeFormatPreference): void => {
  localStorage.setItem(TIME_FORMAT_PREFERENCE_KEY, preference);
};

const DateFormatContext = createContext<ReturnType<typeof createDateFormatter>>(
  createDateFormatter()
);

export const useZonedDateFormat = () => useContext(DateFormatContext);

export const ZonedDateFormatProvider = ({ children }: PropsWithChildren) => {
  const [timeFormatPreference, setTimeFormatPreference] = useState<TimeFormatPreference>(TimeFormatPreference.h12);

  useEffect(() => {
    const savedPreference = loadTimeFormatPreference();
    setTimeFormatPreference(savedPreference);
  }, []);

  const toggleTimeFormat = useCallback(() => {
    const newPreference: TimeFormatPreference = timeFormatPreference === 'military' ? TimeFormatPreference.h12 : TimeFormatPreference.h24;
    setTimeFormatPreference(newPreference);
    saveTimeFormatToLocalStorage(newPreference);
  }, [timeFormatPreference]);

  const formatter = useMemo(() => {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return createDateFormatter(timezone, timeFormatPreference, toggleTimeFormat);
  }, [timeFormatPreference, toggleTimeFormat]);

  return (
    <DateFormatContext.Provider value={formatter}>
      {children}
    </DateFormatContext.Provider>
  )
}
